/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VehicleSystems.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, DialogContentText, IconButton, Stack } from "@mui/material";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import deleteIcon from "../../assets/delete_icon.png";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

import CommonBreadcrumbs from "../../components/customComponentsV2/common/Breadcrumbs/CommonBreadcrumbs";
import CustomSearchbarWithApiV2 from "../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomDataGridV2 from "../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import CustomTabsV2 from "../../components/customComponentsV2/common/tabs/CustomTabsV2";

import { ChipsTextField } from "../../components";
import VehicleComponentsAddRequests from "./VehicleComponentsAddRequests";
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";
import { HandleApiActions } from "../../redux/actions/actionHandler";
import adminSettingsAPI from "../../redux/actions/adminSetttingAPI";
import profileActions from "../../redux/actions/profile";
import customConsole from "../../config/customConsole";
import * as XLSX from "xlsx";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import PropTypes from "prop-types";

const getVehicleSystemsList = (resp) => {
  if (Array.isArray(resp?.data)) return resp.data;
  if (Array.isArray(resp?.response?.data)) return resp.response.data;
  return [];
};

const selectedVehicleSystemShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  name: PropTypes.string,
  components: PropTypes.array,
  allData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
});

function VehicleSystems({ open }) {
  const [tableKey, setTableKey] = useState(0);
  const [totalVehicleSystemsCount, setTotalVehicleSystemsCount] = useState(0);

  useEffect(() => {
    if (open === "vehicle-systems") {
      setTableKey((prev) => prev + 1);
    }
  }, [open]);

  useEffect(() => {
    const handleResize = () => {
      setTableKey((prev) => prev + 1);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const dispatch = useDispatch();

  const vehicleSystemsDetails = useSelector(
    (state) => state.vehicleSystemsReducer,
  );
  const timeZoneDetails = useSelector((state) => state.profileReducer);

  const [page, setPage] = React.useState(1);
  const [rowsPerPage] = React.useState(25);
  const [getVehicleSystems, setGetVehicleSystems] = React.useState([]);
  const [openAddVehicleSystemsDialog, setOpenAddVehicleSystemsDialog] =
    React.useState(false);
  const [openEditVehicleSystemsDialog, setOpenEditVehicleSystemsDialog] =
    React.useState(false);
  const [openDeleteVehicleSystemsDialog, setOpenDeleteVehicleSystemsDialog] =
    React.useState(false);
  const [selectedRow, setSelectedRow] = React.useState(null);
  const [tableRowsData, setTableRowsData] = useState([]);
  const [searchQuery] = useState("");
  const [vehicleRequestsCount, setVehicleRequestsCount] = useState(0);

  useEffect(() => {
    dispatch(
      HandleApiActions({
        ...profileActions.get_coutry_regions,
      }),
    );
  }, [dispatch]);

  useEffect(() => {
    customConsole(timeZoneDetails?.profile?.profile?.region);
  }, [timeZoneDetails]);

  useEffect(() => {
    const vehicleSystemsList = getVehicleSystems?.map((item, index) => ({
      sno: (page - 1) * rowsPerPage + index + 1,
      id: item?.id || index,
      name: item?.name,
      components: item?.components,
      created_at: convertUtcToTimeZone(
        item?.created_at,
        timeZoneDetails?.profile?.profile?.region,
      ),
      updated_at: convertUtcToTimeZone(
        item?.updated_at,
        timeZoneDetails?.profile?.profile?.region,
      ),
      allData: item,
    }));
    setTableRowsData(vehicleSystemsList || []);
  }, [
    getVehicleSystems,
    page,
    rowsPerPage,
    timeZoneDetails?.profile?.profile?.region,
  ]);

  const handleEditClick = useCallback((params) => {
    setSelectedRow(params.row);
    setOpenEditVehicleSystemsDialog(true);
  }, []);

  const handleDeleteClick = useCallback((params) => {
    setSelectedRow(params.row);
    setOpenDeleteVehicleSystemsDialog(true);
  }, []);

  const getVehicleSystemsDetails = useCallback(
    async (pageOverride) => {
      const pageToUse = pageOverride ?? page;
      const apiDetails = adminSettingsAPI.get_vehicle_systems;
      const resp = await dispatch(
        HandleApiActions({
          ...apiDetails,
          show_toast: false,
          params: {
            page_number: pageToUse,
            page_size: rowsPerPage,
          },
        }),
      );
      if (resp?.error) {
        setGetVehicleSystems([]);
        setTableRowsData([]);
        setTotalVehicleSystemsCount(0);
        return { list: [], totalCount: 0 };
      }

      const list = getVehicleSystemsList(resp);

      const totalCount =
        resp?.total_count ??
        resp?.totalItems ??
        resp?.total ??
        (Array.isArray(list) ? list.length : 0);

      setGetVehicleSystems(list);
      setTotalVehicleSystemsCount(totalCount);

      return { list, totalCount };
    },
    [dispatch, page, rowsPerPage],
  );

  useEffect(() => {
    getVehicleSystemsDetails();
  }, [page, rowsPerPage, getVehicleSystemsDetails]);

  useEffect(() => {
    const getVehicleRequestsCount = async () => {
      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_vc_add_requests,
          show_toast: false,
        }),
      );
      if (!resp.error && resp.data) {
        setVehicleRequestsCount(resp.total_count);
      }
    };

    getVehicleRequestsCount();
  }, [dispatch]);

  useEffect(() => {
    customConsole("VehicleSystems Details");
    customConsole(vehicleSystemsDetails);
  }, [vehicleSystemsDetails]);

  const COLUMNS = useMemo(
    () => [
      {
        field: "sno",
        headerName: "S.No",
        flex: 0.3,
        minWidth: 60,
      },
      {
        field: "name",
        headerName: "Name",
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: "components",
        headerName: "Components",
        flex: 1.2,
        minWidth: 200,
      },
      {
        field: "created_at",
        headerName: "Created At",
        flex: 0.6,
        minWidth: 100,
      },
      {
        field: "updated_at",
        headerName: "Updated At",
        flex: 0.6,
        minWidth: 100,
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.4,
        minWidth: 90,
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              size="small"
              sx={{ color: "#000000", p: 0.5 }}
              onClick={(e) => {
                e.stopPropagation();
                handleEditClick(params);
              }}
            >
              <BorderColorIcon fontSize="inherit" sx={{ fontSize: 19 }} />
            </IconButton>
            <IconButton
              size="small"
              sx={{ p: 0 }}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(params);
              }}
            >
              <img
                src={deleteIcon}
                alt="delete"
                style={{ width: 24, height: 24 }}
              />
            </IconButton>
          </Box>
        ),
      },
    ],
    [handleDeleteClick, handleEditClick],
  );

  const toggleDialog = (newOpen) => () => {
    setOpenAddVehicleSystemsDialog(newOpen);
  };

  const handleAddVehicleSystemsClick = () => {
    toggleDialog(true)();
  };

  const filteredRows = useMemo(() => {
    if (!searchQuery) return tableRowsData;
    return tableRowsData.filter((row) =>
      COLUMNS.some((column) =>
        row[column.field]
          ?.toString()
          .toLowerCase()
          .includes(searchQuery.toLowerCase()),
      ),
    );
  }, [searchQuery, tableRowsData, COLUMNS]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  const handleExportToExcel = () => {
    if (!filteredRows || filteredRows.length === 0) {
      return;
    }

    const exportData = filteredRows.map((row) => ({
      "S.No": row.sno,
      Name: row.name,
      Components: Array.isArray(row.components)
        ? row.components.join(", ")
        : row.components || "",
      Created: row.created_at,
      Updated: row.updated_at,
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(exportData);

    XLSX.utils.book_append_sheet(wb, ws, "Vehicle Systems");

    XLSX.writeFile(wb, "Vehicle_Systems.xlsx");
  };

  const handleTabChange = useCallback(() => null, []);

  const breadcrumbItems = [
    { label: "Settings" },
    { label: "Feature Portfolio" },
    { label: "Vehicle System" },
  ];

  return (
    <Box
      sx={{
        margin: 0,
        padding: 0,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F5F5F5",
      }}
    >
      <Box
        sx={{
          padding: { xs: "8px 8px 0px 8px", md: "12px 12px 0px 12px" },
          flexShrink: 0,
        }}
      >
        <CommonBreadcrumbs items={breadcrumbItems} />
      </Box>

      <Box>
        <CustomTabsV2
          headingCentered={false}
          onTabChange={handleTabChange}
          tabs={[
            {
              name: "Vehicle System",
              count: totalVehicleSystemsCount || 0,
              content: (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: {
                        xs: "0 0px 2px 0px",
                        md: "0 0px 4px 0px",
                      },
                      flexShrink: 0,
                      mt: -2.5,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", gap: 1.5, alignItems: "center" }}
                    >
                      <Box>
                        <CustomSearchbarWithApiV2
                          params={{
                            page_number: page,
                            page_size: rowsPerPage,
                          }}
                          apiAction={adminSettingsAPI.get_vehicle_systems}
                          searchKey="name"
                          getDataOnSearch={(data, resp) => {
                            if (resp?.error) {
                              setGetVehicleSystems([]);
                              setTableRowsData([]);
                              setTotalVehicleSystemsCount(0);
                              return;
                            }

                            const searchedList = Array.isArray(data)
                              ? data
                              : [];
                            setGetVehicleSystems(searchedList);
                            setTotalVehicleSystemsCount(
                              resp?.total_count ??
                                resp?.totalItems ??
                                resp?.total ??
                                searchedList.length,
                            );
                          }}
                          placeholder="Search"
                        />
                      </Box>
                      <Box>
                        <AddButtonV2
                          handleOnClick={handleAddVehicleSystemsClick}
                        />
                      </Box>
                    </Box>
                    <Box>
                      <CustomButtonV2
                        startIcon={<FileDownloadOutlinedIcon />}
                        type="submit"
                        size="small"
                        btnName="Export"
                        handleOnClick={handleExportToExcel}
                      />
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      height: "68vh",
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                    key={`vehicle-systems-table-${tableKey}`}
                  >
                    <CustomDataGridV2
                      showPagination
                      rows={filteredRows}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      totalItems={totalVehicleSystemsCount}
                      handlePageChange={(newPage) => setPage(newPage)}
                      columns={COLUMNS}
                      isMultiLine
                    />
                  </Box>
                </>
              ),
            },
            {
              name: "Vehicle System Request",
              count: vehicleRequestsCount,
              content: (
                <Box>
                  <VehicleComponentsAddRequests
                    totalItems={vehicleRequestsCount}
                  />
                </Box>
              ),
            },
          ]}
        />
      </Box>

      <AddVehicleSystems
        open={openAddVehicleSystemsDialog}
        toggleDialog={toggleDialog}
        setPage={setPage}
        refreshVehicleSystems={getVehicleSystemsDetails}
      />
      <EditVehicleSystems
        open={openEditVehicleSystemsDialog}
        setOpenEditVehicleSystemsDialog={setOpenEditVehicleSystemsDialog}
        selectedRow={selectedRow}
        page={page}
        refreshVehicleSystems={getVehicleSystemsDetails}
      />
      <DeleteVehicleSystems
        openDeleteVehicleSystemsDialog={openDeleteVehicleSystemsDialog}
        setOpenDeleteVehicleSystemsDialog={setOpenDeleteVehicleSystemsDialog}
        selectedRow={selectedRow}
        setPage={setPage}
        page={page}
        rowsPerPage={rowsPerPage}
        refreshVehicleSystems={getVehicleSystemsDetails}
      />
    </Box>
  );
}

export default VehicleSystems;

VehicleSystems.propTypes = {
  open: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const AddVehicleSystems = ({
  open,
  toggleDialog,
  setPage,
  refreshVehicleSystems,
}) => {
  const dispatch = useDispatch();

  const [name, setName] = useState("");
  const [components, setComponents] = useState([]);

  const handleClose = () => {
    setName("");
    setComponents([]);
    toggleDialog(false)();
  };

  const handleClickAdd = async () => {
    try {
      const reqObj = {
        current_time: new Date(),
        name: name,
        components: components,
      };

      await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.add_vehicle_systems,
          params: reqObj,
          show_toast: true,
        }),
      );
      toggleDialog(false)();

      setPage(1);
      await refreshVehicleSystems(1);
      setName("");
      setComponents([]);
    } catch (error) {
      console.error("Error adding vehicle system:", error);
    }
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title="Add Vehicle System"
      successButtonName="Add"
      rejectButtonName="Close"
      onClickSuccess={handleClickAdd}
      actionButtonStyle="continuous"
      onClickReject={handleClose}
      onDisabled={!(name.length > 2 && components.length > 0)}
    >
      <Stack spacing={2} sx={{ width: "350px" }}>
        <CustomFormTextFieldV2
          required
          label="Name"
          value={name}
          setValue={(val) => {
            if (val.length <= 35) {
              setName(val);
            }
          }}
          onChange={(e) => {
            setName(e.target.value);
          }}
          maxLength={35}
        />
        <ChipsTextField
          size="small"
          label="Components"
          variant="outlined"
          chips={components}
          setChips={setComponents}
          required
        />
      </Stack>
    </CustomDialogBoxV2>
  );
};

AddVehicleSystems.propTypes = {
  open: PropTypes.bool,
  toggleDialog: PropTypes.func,
  setPage: PropTypes.func,
  refreshVehicleSystems: PropTypes.func,
};

const EditVehicleSystems = ({
  open,
  setOpenEditVehicleSystemsDialog,
  selectedRow,
  page,
  refreshVehicleSystems,
}) => {
  const dispatch = useDispatch();

  const [id, setId] = useState();
  const [editName, setEditName] = useState("");
  const [editComponents, setEditComponents] = useState([]);
  const [initialComponents, setInitialComponents] = useState([]);

  useEffect(() => {
    if (selectedRow) {
      setId(selectedRow?.id || "");
      setEditName(selectedRow?.name || "");
      const comps = selectedRow?.components || [];
      setEditComponents(comps);
      setInitialComponents(comps);
    }
  }, [selectedRow]);

  const normalizeComponents = (list) => {
    const normalized = (Array.isArray(list) ? list : [])
      .map((v) => String(v ?? "").trim())
      .filter(Boolean);
    return Array.from(new Set(normalized)).sort((a, b) =>
      String(a).localeCompare(String(b)),
    );
  };

  const isComponentsValid = normalizeComponents(editComponents).length > 0;
  const isComponentsChanged =
    JSON.stringify(normalizeComponents(editComponents)) !==
    JSON.stringify(normalizeComponents(initialComponents));

  const handleClose = () => {
    setOpenEditVehicleSystemsDialog(false);
  };

  const handleClickEdit = async () => {
    try {
      const reqObj = {
        id: Number(id),
        components: editComponents,
        current_time: new Date().toISOString(),
      };

      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.update_vehicle_systems,
          params: reqObj,
          show_toast: true,
        }),
      );

      if (!resp.error) {
        handleClose();
      }

      await refreshVehicleSystems(page);
    } catch (error) {
      console.error("Error updating vehicle system:", error);
    }
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title="Update Vehicle System"
      successButtonName="Update"
      rejectButtonName="Close"
      onClickSuccess={handleClickEdit}
      actionButtonStyle="continuous"
      onClickReject={handleClose}
      onDisabled={!(selectedRow && isComponentsValid && isComponentsChanged)}
    >
      <Stack spacing={2} sx={{ width: "350px" }}>
        <CustomFormTextFieldV2
          label="Name"
          value={editName}
          setValue={(val) => {
            if (val.length <= 35) {
              setEditName(val);
            }
          }}
          disabled
          required
          maxLength={35}
        />

        <ChipsTextField
          required
          size="small"
          label="Components"
          chips={editComponents}
          setChips={setEditComponents}
        />
      </Stack>
    </CustomDialogBoxV2>
  );
};

EditVehicleSystems.propTypes = {
  open: PropTypes.bool,
  setOpenEditVehicleSystemsDialog: PropTypes.func,
  selectedRow: selectedVehicleSystemShape,
  page: PropTypes.number,
  refreshVehicleSystems: PropTypes.func,
};

const DeleteVehicleSystems = ({
  openDeleteVehicleSystemsDialog,
  selectedRow,
  setOpenDeleteVehicleSystemsDialog,
  setPage,
  page,
  rowsPerPage,
  refreshVehicleSystems,
}) => {
  const dispatch = useDispatch();

  const handleDelete = async () => {
    try {
      const reqObj = {
        current_time: new Date(),
        id: Number.parseInt(selectedRow?.id, 10),
      };

      await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.delete_vehicle_systems,
          params: reqObj,
          show_toast: true,
        }),
      );
      setOpenDeleteVehicleSystemsDialog(false);

      const refreshResult = await refreshVehicleSystems(page);
      const totalCount = refreshResult?.totalCount ?? 0;
      if (page > 1 && totalCount <= (page - 1) * rowsPerPage) {
        const newPage = page - 1;
        setPage(newPage);
        await refreshVehicleSystems(newPage);
      }
    } catch (error) {
      console.error("Error deleting vehicle system:", error);
    }
  };
  return (
    <CustomDialogBoxV2
      open={openDeleteVehicleSystemsDialog}
      onClose={() => setOpenDeleteVehicleSystemsDialog(false)}
      title="Delete Vehicle System"
      successButtonName="Ok"
      rejectButtonName="Cancel"
      onClickSuccess={handleDelete}
      onClickReject={() => setOpenDeleteVehicleSystemsDialog(false)}
      actionButtonStyle="continuous"
    >
      <Box
        sx={{
          width: "350px",
          height: "50px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <DialogContentText>Are you sure want to delete?</DialogContentText>
      </Box>
    </CustomDialogBoxV2>
  );
};

DeleteVehicleSystems.propTypes = {
  openDeleteVehicleSystemsDialog: PropTypes.bool,
  selectedRow: selectedVehicleSystemShape,
  setOpenDeleteVehicleSystemsDialog: PropTypes.func,
  setPage: PropTypes.func,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  refreshVehicleSystems: PropTypes.func,
};
